import { Button, ButtonMode, Checkbox, Modal, ModalWidth, useGlobalModalContext } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import { difference, groupBy } from 'lodash';
import React, { ReactElement, useMemo, useState } from 'react';
import { UserPermissions } from '@creditsights/api-ui-web';
import { useToast } from '../../../../hooks/toast/useToast';
import { RpColumn, RpColumnRenderType } from '../../../../models/RpColumn';
import { EsField } from '../../../../services/dtos/riskProducts/rpColumn.dto';
import { MANAGE_SCORE_COLUMNS, REQUIRED_COLUMNS } from './ManageColumnsModal.constants';
import styles from './ManageColumnsModal.module.scss';
import { trackMonitorManageColumnsUpdate } from '../../../../utils/trackUsageUtils';
import { hasPermissionToCoreScores } from '../../../../utils/permissionsUtils';

const cx = classNames.bind(styles);

export interface ManageColumnsModalProps {
  rpColumns: RpColumn[];
  setCheckedColumns: (rpColumns: EsField[]) => void;
  checkedColumns: EsField[];
  resetColumns: () => void;
  onClose: () => void;
  userPermissions?: UserPermissions;
}

const ManageColumnsModal = ({
  setCheckedColumns,
  checkedColumns,
  rpColumns,
  resetColumns,
  onClose,
  userPermissions,
}: ManageColumnsModalProps): ReactElement => {
  const { closeModal } = useGlobalModalContext();
  const { showToast } = useToast();
  const [initialCheckedColumns] = useState<EsField[]>(checkedColumns);
  const columnGroups = useMemo(() => {
    return groupBy(
      rpColumns
        .filter((rpColumn) => !REQUIRED_COLUMNS.includes(rpColumn.es_field))
        .filter((rpColumn) => rpColumn.renderType !== RpColumnRenderType.SCORE)
        .concat(hasPermissionToCoreScores(userPermissions) ? MANAGE_SCORE_COLUMNS : []),
      'group'
    );
  }, [rpColumns]);

  const onCheck = (columns: RpColumn[], checked: boolean) => {
    trackMonitorManageColumnsUpdate(columns, checked);
    const esFields = columns.map((column) => column.es_field);
    const array = checked
      ? [...checkedColumns, ...esFields]
      : checkedColumns.filter((checkedColumn) => !esFields.includes(checkedColumn));
    setCheckedColumns(array);
  };

  const renderColumnGroup = (group: string, columns: RpColumn[]) => {
    return (
      <div className={cx('group')}>
        <div className={cx('header')} key={group} data-testid="column-group">
          <span className={cx('name')} data-testid="name">
            {group}
          </span>
          <Checkbox
            dataTestId="group-toggle"
            toggle
            label="All"
            aria-label="All"
            onChange={(event, data) => onCheck(columns, !!data.checked)}
            checked={columns.every((column) => checkedColumns.includes(column.es_field))}
            disabled={columns.every((column) => !column.canAccess)}
          />
        </div>
        <div>
          {columns.map((column) => (
            <div className={cx('group-item')} key={column.es_field}>
              <Checkbox
                dataTestId={`${group}-column-toggle`}
                label={column.data_field}
                onChange={(event, data) => onCheck([column], !!data.checked)}
                checked={checkedColumns.includes(column.es_field)}
                disabled={!column.canAccess}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const onModalClose = () => {
    onClose();
    if (
      initialCheckedColumns.length !== checkedColumns.length ||
      difference(checkedColumns.sort(), initialCheckedColumns.sort()).length > 0
    ) {
      showToast('Selected columns have been updated');
    }
    closeModal();
  };

  const headerAction = (
    <Button
      mode={ButtonMode.PLAIN}
      className={cx('header-button')}
      onClick={resetColumns}
      dataTestId="reset-columns-button"
    >
      Reset Columns
    </Button>
  );

  return (
    <Modal
      headerTitle="Manage Columns"
      open
      onClose={onModalClose}
      headerAction={headerAction}
      width={ModalWidth.ExtraLarge}
      dataTestId="manage-columns-modal"
    >
      <div className={cx('body')}>
        <div className={cx('layout')}>
          {Object.entries(columnGroups)?.map(([group, columns]) => renderColumnGroup(group, columns))}
        </div>
      </div>
    </Modal>
  );
};

export default ManageColumnsModal;
